import { ChevronDown, UserX } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useUpdateAppointmentServiceWorker } from "@/domains/appointment/appointments/shared/hooks/use-update-appointment-service-worker";
import { useWorkersQuery } from "@/domains/workers/hooks/use-workers-query";
import { useSalonStore } from "@/lib/store/salon";
import { ColorUtils } from "@/lib/utils/color-utils";
import type { ServiceWithTiming } from "../../utils/service-timing-utils";
import { getWorkerDisplayText } from "../../utils/service-timing-utils";

interface ServiceWorkerSelectorProps {
  service: ServiceWithTiming;
  appointmentId: string;
  onWorkerChange?: (serviceId: string, workerId: string | null) => void;
  disabled?: boolean;
  compact?: boolean;
}

export function ServiceWorkerSelector({
  service,
  appointmentId,
  onWorkerChange,
  disabled = false,
  compact = false,
}: ServiceWorkerSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { currentSalon } = useSalonStore();
  const { data: workersData } = useWorkersQuery({ salonId: currentSalon?.id });
  const { updateAppointmentServiceWorker: updateServiceWorker, isUpdating: isPending } =
    useUpdateAppointmentServiceWorker();

  const availableWorkers = useMemo(() => {
    return workersData?.workers?.workers || [];
  }, [workersData]);

  const currentWorker = service.worker;
  const workerColor = ColorUtils.getColorStyle(currentWorker?.color);
  const workerText = getWorkerDisplayText(currentWorker, compact);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleWorkerSelect = async (workerId: string | null) => {
    try {
      await updateServiceWorker(appointmentId, service.id, workerId);

      onWorkerChange?.(service.id, workerId);
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to update service worker:", error);
    }
  };

  const handleToggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled || isPending) return;
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Worker Indicator Button */}
      <button
        type="button"
        onClick={handleToggleDropdown}
        disabled={disabled || isPending}
        className={`
          relative flex items-center space-x-1 rounded-full border-2 border-background shadow-sm
          transition-all duration-150 group
          ${compact ? "w-4 h-4" : "w-5 h-5 px-2 py-1"}
          ${disabled || isPending ? "opacity-50 cursor-not-allowed" : "hover:scale-105 cursor-pointer"}
          ${workerColor.className}
        `}
        style={workerColor.style}
        title={`${workerText} - Click to change worker`}
      >
        {/* Worker indicator content */}
        {compact ? (
          <span className="text-[7px] font-bold text-white mix-blend-difference">
            {workerText.charAt(0)}
          </span>
        ) : currentWorker ? (
          <>
            <span className="text-[8px] font-bold text-white mix-blend-difference truncate">
              {workerText}
            </span>
            <ChevronDown className="w-2 h-2 text-white mix-blend-difference opacity-0 group-hover:opacity-100 transition-opacity" />
          </>
        ) : (
          <>
            <UserX className="w-3 h-3 text-muted-foreground" />
            <ChevronDown className="w-2 h-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </>
        )}

        {/* Loading indicator */}
        {isPending && (
          <div className="absolute inset-0 rounded-full bg-background/50 flex items-center justify-center">
            <div className="w-2 h-2 border border-foreground border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-[120px] max-w-[200px]">
          <div className="py-1">
            {/* Unassign option */}
            <button
              type="button"
              onClick={() => handleWorkerSelect(null)}
              className="w-full flex items-center space-x-2 px-3 py-2 text-left hover:bg-accent text-sm"
            >
              <UserX className="w-3 h-3 text-muted-foreground" />
              <span>Unassigned</span>
            </button>

            {/* Divider */}
            {availableWorkers.length > 0 && <div className="border-t border-border my-1" />}

            {/* Available workers */}
            {availableWorkers.map((worker) => {
              const isSelected = currentWorker?.id === worker.id;
              const workerDisplayColor = ColorUtils.getColorStyle(worker.color);
              const displayName =
                worker.displayName ||
                (worker.user
                  ? `${worker.user.firstName} ${worker.user.lastName}`.trim()
                  : "Unknown Worker");

              return (
                <button
                  type="button"
                  key={worker.id}
                  onClick={() => handleWorkerSelect(worker.id)}
                  className={`
                    w-full flex items-center space-x-2 px-3 py-2 text-left hover:bg-accent text-sm
                    ${isSelected ? "bg-accent font-medium" : ""}
                  `}
                >
                  <div
                    className={`w-3 h-3 rounded-full border border-background ${workerDisplayColor.className}`}
                    style={workerDisplayColor.style}
                  />
                  <span className="truncate">{displayName}</span>
                  {isSelected && <div className="ml-auto w-1 h-1 bg-primary rounded-full" />}
                </button>
              );
            })}

            {/* No workers available */}
            {availableWorkers.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">No workers available</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
